import { FileText } from "lucide-react";
import { cloneElement, type ReactElement, useState } from "react";

import { useGetDocumentViewUrl } from "@elation/api-client/src/stitch-web";

import { Button } from "@src/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@src/shared/components/ui/sheet";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { cn } from "@src/shared/lib/utils";

export interface DocumentViewerProps {
  defaultOpen?: boolean;
  documentId: number;
  fileName: string;
  uploadedAtLabel?: string;
  trigger?: ReactElement;
  triggerTooltip?: string;
}

export function DocumentViewer({
  defaultOpen = false,
  documentId,
  fileName,
  uploadedAtLabel,
  trigger,
  triggerTooltip,
}: DocumentViewerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const query = useGetDocumentViewUrl(documentId, {
    query: {
      enabled: open,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  });
  const viewUrl = query.isError ? null : query.data?.data.viewUrl;

  const triggerElement = trigger ? (
    cloneElement(trigger, {
      title: triggerTooltip,
    })
  ) : (
    <Button
      type="button"
      title={triggerTooltip}
      variant="ghost"
      className="h-auto min-w-0 justify-start gap-1 rounded-[6px] px-0 py-0 font-mono text-[11px] text-primary hover:bg-transparent hover:text-primary"
    >
      <FileText className="size-3" aria-hidden="true" />
      <span className="truncate">{fileName}</span>
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{triggerElement}</SheetTrigger>
      <SheetContent
        side="right"
        className="flex w-[min(100vw,50vw)] max-w-none flex-col gap-0 rounded-l-[16px] border-border bg-background p-0 shadow-soft sm:max-w-none"
      >
        <SheetHeader className="border-b border-border px-4 py-3 text-left">
          <SheetTitle className="pr-8 text-[13.5px] font-semibold leading-tight">
            {fileName}
          </SheetTitle>
          <SheetDescription className="font-mono text-[10.5px] leading-none">
            {uploadedAtLabel ? `Uploaded ${uploadedAtLabel} · ` : null}
            {fileName}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 bg-muted/30 p-4">
          <div className="h-full overflow-hidden rounded-[12px] border border-border bg-background">
            {query.isLoading ? <DocumentViewerSkeleton /> : null}
            {query.isError ? (
              <DocumentViewerMessage>
                Unable to load this document preview.
              </DocumentViewerMessage>
            ) : null}
            {viewUrl ? (
              <iframe
                title={`Preview of ${fileName}`}
                src={viewUrl}
                className={cn(
                  "h-full min-h-[520px] w-full border-0 bg-background",
                  query.isFetching && "opacity-70",
                )}
              />
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DocumentViewerSkeleton() {
  return (
    <div className="space-y-4 p-6" aria-label="Loading document preview">
      <div className="space-y-2">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-3 w-48" />
      </div>
      <div className="space-y-2 pt-6">
        <Skeleton className="h-2 w-28" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-2 w-11/12" />
        <Skeleton className="h-2 w-4/5" />
      </div>
      <div className="space-y-2 pt-6">
        <Skeleton className="h-2 w-40" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-2 w-10/12" />
      </div>
    </div>
  );
}

function DocumentViewerMessage({ children }: { children: string }) {
  return (
    <div className="flex min-h-[520px] items-center justify-center px-6 text-center text-[13px] text-muted-foreground">
      {children}
    </div>
  );
}
